import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  GUEST_GUIDE_DEFAULT_LANGUAGE,
  GUEST_GUIDE_LANGUAGE_OPTIONS,
  normalizeGuestGuideLanguage
} from '../constants/guestArtistGuide';
import './GuestConditionsPage.css';
import useGuestGuide from '../hooks/useGuestGuide';
import GuestGuideLoadState from '../components/GuestGuideLoadState';

const HOME_LINK_LABEL = '<-';

function BulletList({ items }) {
  if (!items?.length) {
    return null;
  }

  return (
    <ul className="guest-guide-list">
      {items.map(item => (
        <li key={typeof item === 'string' ? item : item.text}>
          {typeof item === 'string' ? (
            item
          ) : (
            <a href={item.href} target="_blank" rel="noopener noreferrer">
              {item.text}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function Paragraphs({ paragraphs }) {
  if (!paragraphs?.length) {
    return null;
  }

  return paragraphs.map(paragraph => (
    <p key={paragraph} className="guest-guide-paragraph">
      {paragraph}
    </p>
  ));
}

function HomeLink({ placement, onBack }) {
  return (
    <button
      type="button"
      className={`guest-guide-home-link guest-guide-home-link--${placement}`}
      aria-label="Go back"
      onClick={onBack}
    >
      {HOME_LINK_LABEL}
    </button>
  );
}

function GuideSummary({ guide }) {
  if (!guide.summaryItems?.length) {
    return null;
  }

  return (
    <section className="guest-guide-summary" aria-labelledby="guest-guide-summary-title">
      <h2 id="guest-guide-summary-title">{guide.summaryTitle}</h2>
      <BulletList items={guide.summaryItems} />
    </section>
  );
}

function BedComparison({ comparison }) {
  if (!comparison) return null;
  return (
    <table className="guest-guide-comparison">
      <thead>
        <tr>
          <td />
          {comparison.options.map(option => <th scope="col" key={option.title}>{option.title}</th>)}
        </tr>
      </thead>
      <tbody>
        {comparison.labels.map((label, index) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            {comparison.options.map(option => <td key={option.title}>{option.values[index]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GuideNotice({ notice, variant }) {
  if (!notice) {
    return null;
  }

  const titleId = `guest-guide-${variant}-title`;

  return (
    <section
      className={`guest-guide-notice guest-guide-notice--${variant}`}
      aria-labelledby={titleId}
    >
      <h2 id={titleId}>{notice.title}</h2>
      <Paragraphs paragraphs={notice.paragraphs} />
      <BulletList items={notice.items} />
    </section>
  );
}

export default function GuestConditionsPage() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedLanguage = normalizeGuestGuideLanguage(lang);
  const { document: guideDocument, loading, retry } = useGuestGuide(selectedLanguage);
  const guide = guideDocument?.webGuide;

  const handleLanguageChange = (nextLanguage) => {
    navigate(
      nextLanguage === GUEST_GUIDE_DEFAULT_LANGUAGE ? '/guest-conditions' : `/guest-conditions/${nextLanguage}`,
      { state: location.state }
    );
  };

  const handleBack = () => {
    const returnTo = location.state?.returnTo;

    if (returnTo) {
      navigate(returnTo);
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  return (
    <div className="guest-conditions-page-wrapper">
      <main className="guest-guide-document">
        <HomeLink placement="top" onBack={handleBack} />

        <nav className="guest-guide-language-switcher" aria-label="Language">
          {GUEST_GUIDE_LANGUAGE_OPTIONS.map(option => (
            <button
              key={option.id}
              type="button"
              className={option.id === selectedLanguage ? 'is-active' : ''}
              onClick={() => handleLanguageChange(option.id)}
            >
              {option.label}
            </button>
          ))}
        </nav>

        {guide ? <>
          <header className="guest-guide-header">
            <h1>{guide.title}</h1>
            {guide.intro && <p>{guide.intro}</p>}
          </header>

          <GuideSummary guide={guide} />

          {guide.sections.map((section, index) => (
            <section key={section.title} className="guest-guide-section">
              <h2>{section.title}</h2>
              <Paragraphs paragraphs={section.paragraphs} />
              <BulletList items={section.items} />
              <BedComparison comparison={section.comparison} />

              {section.subsections?.map(subsection => (
                <div key={subsection.title} className="guest-guide-subsection">
                  <h3>{subsection.title}</h3>
                  {!section.comparison && <BulletList items={subsection.items} />}
                  <Paragraphs paragraphs={subsection.paragraphs} />
                </div>
              ))}

              {index < guide.sections.length - 1 && <hr className="guest-guide-divider" />}
            </section>
          ))}

          <GuideNotice notice={guide.studioNotice} variant="studio" />
          <GuideNotice notice={guide.commitmentNotice} variant="commitment" />

          <footer className="guest-guide-footer">
            {guideDocument.updated}
          </footer>

        </> : <GuestGuideLoadState language={selectedLanguage} loading={loading} retry={retry} />}

        <HomeLink placement="bottom" onBack={handleBack} />
      </main>
    </div>
  );
}
